import {
  Card,
  CardContent,
  CardMedia,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { MANAGE_ALERTS, ORG_MANAGE_ALERT_SETTINGS } from '@constants/routes';

import { useCuration } from '@hooks/useCuration';
import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';

import AlertNotificationImg from '@images/info-drawer/alerts/AlertNotification.png';
import AlertParameterImg from '@images/info-drawer/alerts/AlertParameter.png';
import AlertRecipientImg from '@images/info-drawer/alerts/AlertRecipient.png';

const messages = defineMessages({
  purpose: {
    id: 'drawer.alertSettings.purpose',
    defaultMessage:
      'Alerts are used to notify practitioners of their patient’s health measurements that are out of range.',
  },
  instructions1: {
    id: 'drawer.alertSettings.instructions1',
    defaultMessage:
      "Click the 'New Alert' button on the right to create an alert for {name}.",
  },
  instructions2: {
    id: 'drawer.alertSettings.instructions2',
    defaultMessage: 'An alert has 3 configurable settings:',
  },
  'notification.title': {
    id: 'drawer.alertSettings.notification.title',
    defaultMessage: 'I) Alert Notifications',
  },
  'notification.subtitle': {
    id: 'drawer.alertSettings.notification.subtitle',
    defaultMessage:
      'If enabled, alert notifications will be sent to recipients via email/sms and an alert entry will be created in Engage. If disabled, only the alert entry will be created.',
  },
  'parameter.title': {
    id: 'drawer.alertSettings.parameter.title',
    defaultMessage: 'II) Alert Parameters',
  },
  'parameter.subtitle': {
    id: 'drawer.alertSettings.parameter.subtitle',
    defaultMessage:
      'Select the range(s) a health reading has to be in to trigger an alert.',
  },
  'recipient.title': {
    id: 'drawer.alertSettings.recipient.title',
    defaultMessage: 'III) Alert Recipients',
  },
  'recipient.subtitle': {
    id: 'drawer.alertSettings.recipient.subtitle',
    defaultMessage:
      'Verified user recipients need to be manually added to the list. If a global organization recipient is configured, they are automatically a recipient of all alerts.',
  },
  setup1: {
    id: 'drawer.alertSettings.setup1',
    defaultMessage: 'If you haven’t setup up a ',
  },
  setup2: {
    id: 'drawer.alertSettings.setup2',
    defaultMessage: 'user',
  },
  setup3: {
    id: 'drawer.alertSettings.setup3',
    defaultMessage: ' or an ',
  },
  setup4: {
    id: 'drawer.alertSettings.setup4',
    defaultMessage: 'organization',
  },
  setup5: {
    id: 'drawer.alertSettings.setup5',
    defaultMessage:
      ' alert recipient, click the {count, plural, one {link} other {links}} to set {count, plural, one {it} other {them}} up first.',
  },
});

export default function AlertSettingDrawerContent() {
  const { formatMessage } = useIntl();
  const { navigateRoute } = useNavigate();
  const {
    state: { activeProfile, isOrgAdmin },
  } = useSession();
  const { reset } = useCuration();

  const steps = [
    {
      title: messages['notification.title'],
      subtitle: messages['notification.subtitle'],
      image: AlertNotificationImg,
    },
    {
      title: messages['parameter.title'],
      subtitle: messages['parameter.subtitle'],
      image: AlertParameterImg,
    },
    {
      title: messages['recipient.title'],
      subtitle: messages['recipient.subtitle'],
      image: AlertRecipientImg,
    },
  ];

  const linkSxProps = {
    textDecoration: 'none',
  };

  return (
    <Stack>
      <Typography variant="body1">{formatMessage(messages.purpose)}</Typography>

      <Typography color="text.secondary" variant="body2">
        {formatMessage(messages.setup1)}
        <Link
          onClick={async () => {
            /* Set up alert recipient settings for logged in user, not active patient */
            await reset();
            navigateRoute(`${MANAGE_ALERTS}`);
          }}
          sx={{ ...linkSxProps }}
        >
          {formatMessage(messages.setup2)}
        </Link>
        {isOrgAdmin && (
          <>
            {formatMessage(messages.setup3)}
            <Link
              onClick={async () =>
                navigateRoute(`${ORG_MANAGE_ALERT_SETTINGS}`)
              }
              sx={{ ...linkSxProps }}
            >
              {formatMessage(messages.setup4)}
            </Link>
          </>
        )}
        {formatMessage(messages.setup5, {
          count: isOrgAdmin ? 5 : 1,
        })}
      </Typography>

      <Typography variant="body1">
        {formatMessage(messages.instructions1, {
          name: activeProfile?.first_name,
        })}
      </Typography>
      <Typography variant="body1">
        {formatMessage(messages.instructions2)}
      </Typography>
      {steps.map(({ image, title, subtitle }) => (
        <Card key={title.id}>
          <CardContent>
            <Typography color="purple.main" variant="body1">
              {formatMessage(title)}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {formatMessage(subtitle)}
            </Typography>
            <CardMedia
              component="img"
              image={image}
              alt="Instructions"
              sx={{ boxShadow: (theme) => theme.shadows[1] }}
            />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
